import React, { useState } from "react";
import {
  CheckCircle2,
  Loader2,
  LockKeyhole,
} from "lucide-react";

export function SecurePinCard({
  onConfirm,
  isSubmitting = false,
  fundingMethod = "bank",
}) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");

  const isValid = /^\d{6}$/.test(pin);

  async function handleSubmit(event) {
    event?.preventDefault();

    if (!isValid || isSubmitting) {
      if (!isValid) {
        setError(
          "Enter your 6-digit transaction PIN."
        );
      }
      return;
    }

    setError("");

    try {
      await onConfirm(pin);

      /*
       * Clear the PIN immediately after a successful
       * authorization attempt.
       *
       * It is never written into chat state/history.
       */
      setPin("");
    } catch {
      /*
       * The hook/reducer owns the customer-safe API
       * error message. Keep this component concerned
       * only with the secure input.
       */
      setPin("");
    }
  }

  function handleChange(event) {
    const value = event.target.value
      .replace(/\D/g, "")
      .slice(0, 6);

    setPin(value);

    if (error) {
      setError("");
    }
  }

  return (
    <div className="w-full max-w-[430px] rounded-3xl border border-[#0A4730]/10 bg-white p-5 shadow-[0_8px_30px_rgba(10,71,48,0.08)]">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#C0F6B7]/50 text-[#0A4730]">
          <LockKeyhole size={19} />
        </div>

        <div>
          <h3 className="text-sm font-semibold text-[#0A4730]">
            Authorise transfer
          </h3>

          <p className="mt-1 text-xs leading-5 text-[#0A2540]/55">
            Enter your 6-digit transaction PIN.
            Your PIN won't appear in this chat.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-5"
      >
        <label
          htmlFor="chat-transaction-pin"
          className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#0A4730]/60"
        >
          Transaction PIN
        </label>

        <input
          id="chat-transaction-pin"
          aria-label="6-digit transaction PIN"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={6}
          value={pin}
          disabled={isSubmitting}
          onChange={handleChange}
          placeholder="••••••"
          className="h-14 w-full rounded-2xl border border-slate-200 bg-[#FDFDFD] px-4 text-center text-xl font-semibold tracking-[0.55em] text-[#0A2540] outline-none transition focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 disabled:cursor-not-allowed disabled:opacity-60"
        />

        {error && (
          <p
            role="alert"
            className="mt-2 text-xs text-red-500"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={!isValid || isSubmitting}
          className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#0A4730] text-sm font-semibold text-white transition hover:bg-[#083b28] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isSubmitting ? (
            <>
              <Loader2
                size={16}
                className="animate-spin"
              />
              Authorising…
            </>
          ) : (
            <>
              <CheckCircle2 size={16} />
              {fundingMethod === "card"
                ? "Continue to card payment"
                : "Confirm transfer"}
            </>
          )}
        </button>
      </form>
    </div>
  );
}